/**
 * CommentaryPanel Component
 *
 * Expandable panel showing commentaries for a verse:
 * - Lazy loads commentary on first expand
 * - Groups by category (Rashi, Ibn Ezra, etc.)
 * - Shows Hebrew and English text
 * - Caches after first load
 */

import { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { getVerseCommentary } from '@/services/sefariaTextService';
import { getCachedCommentary } from '@/services/sefariaCache';
import type { SefariaLink, CommentaryPanelProps, CommentaryGroup } from '@/types/sefaria';

// ============================================================
// Commentary Item Component
// ============================================================

interface CommentaryItemProps {
  link: SefariaLink;
}

function CommentaryItem({ link }: CommentaryItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-[#8e4585]" />
          <span className="font-medium text-slate-700">
            {link.collectiveTitle.en || 'Commentary'}
          </span>
          {link.collectiveTitle.he && (
            <span className="text-slate-400 text-sm" dir="rtl" lang="he">
              ({link.collectiveTitle.he})
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={16} className="text-slate-400" />
        ) : (
          <ChevronDown size={16} className="text-slate-400" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {/* Hebrew text */}
              {link.hebrew && (
                <div
                  className="text-lg leading-relaxed text-slate-700 bg-slate-50 p-3 rounded-lg"
                  dir="rtl"
                  lang="he"
                  style={{ fontFamily: "'Frank Ruhl Libre', serif" }}
                >
                  {link.hebrew}
                </div>
              )}

              {/* English translation */}
              {link.english && (
                <div className="text-sm leading-relaxed text-slate-600">{link.english}</div>
              )}

              {/* Reference */}
              <div className="text-xs text-slate-400">{link.ref}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// Commentary Category Group
// ============================================================

interface CommentaryCategoryProps {
  group: CommentaryGroup;
}

function CommentaryCategory({ group }: CommentaryCategoryProps) {
  return (
    <div className="mb-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 px-4 py-2 bg-slate-100">
        {group.category} ({group.links.length})
      </h4>
      <div className="divide-y divide-slate-100">
        {group.links.map((link, index) => (
          <CommentaryItem key={`${link.ref}-${index}`} link={link} />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

function CommentaryPanelComponent({ verseRef, isExpanded, onClose }: CommentaryPanelProps) {
  const [commentary, setCommentary] = useState<SefariaLink[] | null>(() =>
    getCachedCommentary(verseRef)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Fetch commentary when expanded (lazy loading)
  useEffect(() => {
    if (!isExpanded) return;
    if (hasFetched.current) return;
    if (commentary) return;

    hasFetched.current = true;
    setLoading(true);
    setError(null);

    getVerseCommentary(verseRef)
      .then((data) => {
        setCommentary(data);
      })
      .catch((err) => {
        console.error('Failed to fetch commentary:', err);
        setError('Failed to load commentary');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [verseRef, isExpanded, commentary]);

  // Group commentaries by category
  const groupedCommentary: CommentaryGroup[] = commentary
    ? Object.entries(
        commentary.reduce(
          (groups, link) => {
            const category = link.category || 'Other';
            if (!groups[category]) {
              groups[category] = [];
            }
            groups[category].push(link);
            return groups;
          },
          {} as Record<string, SefariaLink[]>
        )
      )
        .map(([category, links]) => ({ category, links }))
        .sort((a, b) => {
          // Sort by priority: Commentary first, then alphabetically
          if (a.category === 'Commentary') return -1;
          if (b.category === 'Commentary') return 1;
          return a.category.localeCompare(b.category);
        })
    : [];

  if (!isExpanded) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden bg-white border-l-4 border-[#c075c0] ml-10 mt-2 rounded-lg shadow-sm"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#f8e8f8] border-b border-[#e0c0e0]">
        <h3 className="font-bold text-[#63345e] flex items-center gap-2">
          <BookOpen size={18} />
          Commentary for {verseRef}
        </h3>
        <button
          onClick={onClose}
          className="text-[#8e4585] hover:text-[#63345e] text-sm font-medium"
        >
          Close
        </button>
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader size={24} className="animate-spin mr-2" />
            <span>Loading commentary...</span>
          </div>
        )}

        {error && (
          <div className="px-4 py-8 text-center text-red-500">
            <p>{error}</p>
            <button
              onClick={() => {
                hasFetched.current = false;
                setError(null);
                setCommentary(null);
              }}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && commentary && commentary.length === 0 && (
          <div className="px-4 py-8 text-center text-slate-400">
            <BookOpen size={32} className="mx-auto mb-2 opacity-50" />
            <p>No commentary available for this verse.</p>
          </div>
        )}

        {!loading && !error && groupedCommentary.length > 0 && (
          <div className="py-2">
            {groupedCommentary.map((group) => (
              <CommentaryCategory key={group.category} group={group} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export const CommentaryPanel = memo(CommentaryPanelComponent);
